import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Switch, Checkbox, Button, Row, Col, Typography, Space } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { createChecklistTemplate, updateChecklistTemplate } from "../../network/api";
import { notify, serverError } from "../../utils/notify";
import { VISIT_TYPE_OPTIONS, CHECKLIST_ITEM_TYPE_OPTIONS } from "../../constants/visits";

const { Text } = Typography;

// Map a template's items (options as array) → form shape (options as comma string), and back.
const toForm = (tpl) => ({
  name: tpl?.name || "",
  visitType: tpl?.visitType,
  description: tpl?.description || "",
  isActive: tpl?.isActive !== false,
  items: (tpl?.items || []).map((it) => ({
    key: it.key,
    label: it.label,
    type: it.type || "boolean",
    required: !!it.required,
    options: Array.isArray(it.options) ? it.options.join(", ") : "",
  })),
});

export default function ChecklistTemplateModal({ open, template, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const editing = !!template?._id;

  useEffect(() => {
    if (open) form.setFieldsValue(toForm(template));
  }, [open, template, form]);

  const handleOk = async () => {
    try {
      const v = await form.validateFields();
      setLoading(true);
      const payload = {
        name: v.name,
        visitType: v.visitType,
        description: v.description,
        isActive: v.isActive,
        items: (v.items || []).map((it) => ({
          key: it.key,
          label: it.label,
          type: it.type,
          required: !!it.required,
          options:
            it.type === "select" && it.options
              ? it.options.split(",").map((s) => s.trim()).filter(Boolean)
              : undefined,
        })),
      };
      if (editing) await updateChecklistTemplate(template._id, payload);
      else await createChecklistTemplate(payload);
      notify.success(editing ? "Template updated" : "Template created");
      form.resetFields();
      onSaved();
      onClose();
    } catch (error) {
      if (error?.errorFields) {
        setLoading(false);
        return;
      }
      notify.error(serverError(error, "Failed to save template"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={editing ? "Edit Checklist Template" : "New Checklist Template"}
      open={open}
      onOk={handleOk}
      confirmLoading={loading}
      okText="Save"
      width={760}
      onCancel={() => { form.resetFields(); onClose(); }}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="name" label="Name" rules={[{ required: true, message: "Enter a name" }]}>
              <Input placeholder="e.g. New Install Checklist" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="visitType" label="Visit type" rules={[{ required: true, message: "Select a type" }]}>
              <Select options={VISIT_TYPE_OPTIONS} placeholder="Type" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={4}>
            <Form.Item name="isActive" label="Active" valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="description" label="Description">
          <Input placeholder="Optional" />
        </Form.Item>

        <Text strong>Checklist items</Text>
        <div style={{ marginTop: 8 }}>
          <Row gutter={8} style={{ marginBottom: 4 }}>
            <Col span={5}><Text type="secondary" style={{ fontSize: 12 }}>Key</Text></Col>
            <Col span={7}><Text type="secondary" style={{ fontSize: 12 }}>Label</Text></Col>
            <Col span={5}><Text type="secondary" style={{ fontSize: 12 }}>Type</Text></Col>
            <Col span={3}><Text type="secondary" style={{ fontSize: 12 }}>Req.</Text></Col>
            <Col span={4}><Text type="secondary" style={{ fontSize: 12 }}>Options</Text></Col>
          </Row>
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Row key={key} gutter={8} align="top">
                    <Col span={5}>
                      <Form.Item {...rest} name={[name, "key"]} rules={[{ required: true, message: "key" }]}>
                        <Input placeholder="lens_clean" />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item {...rest} name={[name, "label"]} rules={[{ required: true, message: "label" }]}>
                        <Input placeholder="Lens cleaned" />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item {...rest} name={[name, "type"]} initialValue="boolean">
                        <Select options={CHECKLIST_ITEM_TYPE_OPTIONS} />
                      </Form.Item>
                    </Col>
                    <Col span={3} style={{ textAlign: "center" }}>
                      <Form.Item {...rest} name={[name, "required"]} valuePropName="checked">
                        <Checkbox />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Form.Item {...rest} name={[name, "options"]}>
                        <Input placeholder="a, b, c" />
                      </Form.Item>
                    </Col>
                    <Col span={1}>
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ type: "boolean", required: false })} block>
                  Add item
                </Button>
              </>
            )}
          </Form.List>
        </div>
      </Form>
    </Modal>
  );
}
